import React from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { getMsalInstance, loginRequest, setFakeEmail } from './auth/auth';
import {
    clearAuthRecoveryFlag,
    clearLocalAuthAndLogin,
    isAuthRelatedError,
} from './auth/authRecovery';
import App from './App';
import { PRA_SETTINGS } from './config/runtimeConfig';
import { setCurrentUserIdProvider } from './api/client';

import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import 'react-datepicker/dist/react-datepicker.css';

import './style/base.css';
import './style/invoices.css';
import './style/table.css';
import './style/dashboard.css';
import './style/userNorms.css';
import './style/contracts.css';

let currentUserId = null;

setCurrentUserIdProvider(() => currentUserId);

export const setCurrentUserId = (id) => {
    currentUserId = id;
};

export const getCurrentUserId = () => currentUserId;

function renderAuthRecoveryUi(rootEl, err) {
    const detail = err?.message || err?.errorCode || 'authentication error';
    rootEl.innerHTML = `
        <div style="padding: 40px; max-width: 32rem; font-family: system-ui, sans-serif;">
            <p style="color: #b00020; margin: 0 0 12px;">Inloggen mislukt (${detail}).</p>
            <p style="margin: 0 0 16px;">Klik hieronder om lokaal uit te loggen en meteen opnieuw in te loggen.</p>
            <button type="button" id="pra-auth-retry"
                style="padding: 10px 16px; cursor: pointer; font-size: 1rem;">
                Opnieuw inloggen
            </button>
        </div>
    `;
    rootEl.querySelector('#pra-auth-retry')?.addEventListener('click', () => {
        const button = rootEl.querySelector('#pra-auth-retry');
        if (button) {
            button.disabled = true;
            button.textContent = 'Bezig met opnieuw inloggen…';
        }
        getMsalInstance()
            .then(instance => clearLocalAuthAndLogin(instance, loginRequest, { force: true }))
            .catch(retryErr => {
                console.error('Auth recovery retry failed:', retryErr);
                renderAuthRecoveryUi(rootEl, retryErr);
            });
    });
}

async function ensureAuthenticated(msalInstance) {
    let redirectResult = null;

    try {
        redirectResult = await msalInstance.handleRedirectPromise();
    } catch (err) {
        console.error('MSAL redirect error:', err);
        const started = await clearLocalAuthAndLogin(msalInstance, loginRequest);
        if (started) return false;
        throw err;
    }

    if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
        clearAuthRecoveryFlag();
        return true;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        clearAuthRecoveryFlag();
        return true;
    }

    try {
        await msalInstance.loginRedirect(loginRequest);
        return false;
    } catch (err) {
        console.error('MSAL loginRedirect error:', err);
        if (isAuthRelatedError(err)) {
            const started = await clearLocalAuthAndLogin(msalInstance, loginRequest);
            if (started) return false;
        }
        throw err;
    }
}

async function bootstrapApp() {
    const fakeUserEmail = import.meta.env.VITE_FAKE_USER_AS || PRA_SETTINGS?.fakeUserAs || null;

    if (fakeUserEmail) {
        console.log(`[fakeUserAs] Bypassing MSAL — faking login as: ${fakeUserEmail}`);
        setFakeEmail(fakeUserEmail);

        const root = createRoot(document.getElementById('root'));
        root.render(React.createElement(App, { fakeUser: true }));
        return;
    }

    const msalInstance = await getMsalInstance();
    const signedIn = await ensureAuthenticated(msalInstance);
    if (!signedIn) {
        // loginRedirect / recovery redirect in progress
        return;
    }

    const root = createRoot(document.getElementById('root'));
    root.render(
        React.createElement(
            MsalProvider,
            { instance: msalInstance },
            React.createElement(App, null)
        )
    );
}

bootstrapApp().catch(err => {
    console.error('Failed to bootstrap app:', err);
    const rootEl = document.getElementById('root');
    if (!rootEl) return;

    if (isAuthRelatedError(err)) {
        renderAuthRecoveryUi(rootEl, err);
        return;
    }

    rootEl.innerHTML =
        '<div style="padding: 40px; color: red;">Fout bij het opstarten van de applicatie. Probeer de pagina te vernieuwen.</div>';
});
